import React from 'react';
import { StyleSheet } from 'react-native';
import { Typography, Box, Stack, SxProps, Theme } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useThemeContext } from '../../hook/useThemeManager';
import { ColorScheme, theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { convertMinuteSecondsDays, createFullName, formatNumberWithK } from '../../utils/AppUtils';
import { IProjectSummary } from '../../types/global/media.types';
import { EsPlayButton } from '../EsButtons/EsPlayButton';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import { IUserSummary } from '../../types/global/helper';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';

export interface IMediaCard {
    media: IProjectSummary;
    onMediaCardPressed?: (project: IProjectSummary) => void;
    isDetailsOnly?: boolean;
    sx?: SxProps<Theme>;
}

const MediaCard: React.FC<IMediaCard> = ({ media, onMediaCardPressed, isDetailsOnly, sx }) => {
    const { themeStyle } = useThemeContext();
    const styles = dynamicStyles(themeStyle);

    return (
        <Box
            sx={{
                mb: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.6,
                '.media-thumb': {
                    transition: 'transform 700ms ease',
                },
                '.media-overlay': {
                    opacity: 0,
                    transform: 'scale(1)',
                    transition: 'opacity 300ms ease, transform 700ms ease',
                },
                '&:hover .media-thumb': {
                    transform: 'scale(1.08)',
                },
                '&:hover .media-overlay': {
                    opacity: 1,
                    transform: 'scale(1.08)',
                },
            }}>
            <Box
                onClick={() => onMediaCardPressed && onMediaCardPressed(media)}
                sx={{
                    width: '100%',
                    aspectRatio: '16/10',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-end',
                    borderRadius: 3,
                    border: 'none',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    ...sx,
                }}>
                <CustomLazyLoadImage
                    src={media.thumbnail ? media.thumbnail : media.feature_image}
                    className="media-thumb"
                    style={{
                        width: '100%',
                        height: '100%',
                        backgroundSize: 'contain',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        objectFit: 'cover',
                        zIndex: 0,
                        borderRadius: '12px',
                    }}
                />
                <Box
                    className="media-overlay"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '12px',
                        pointerEvents: 'none',
                        background: 'linear-gradient(180deg, rgba(0,0,0,0) 38%, rgba(0,0,0,0.82) 100%)',
                    }}
                />
            </Box>
            {isDetailsOnly && (
                <Box
                    sx={{
                        zIndex: 2,
                        height: '20px',
                        width: '20px',
                    }}>
                    {isDetailsOnly && (
                        <EsPlayButton
                            onClick={() => onMediaCardPressed && onMediaCardPressed(media)}
                            sx={{ position: 'relative', height: '20px', width: '20px' }}
                        />
                    )}
                </Box>
            )}
            {isDetailsOnly && (
                <Box sx={{ gap: '2px', marginLeft: 1 }}>
                    <Typography style={styles.title}>{media.title}</Typography>
                </Box>
            )}
            {!isDetailsOnly && (
                <Stack direction="row" alignItems="center" justifyContent="space-between" px={0.5}>
                    <Stack direction="row" alignItems="center" sx={{ minWidth: 0, flex: 1, mr: 1.2 }}>
                        <ThspianAvatar
                            user={media.created_by as unknown as IUserSummary}
                            enableDefaultNavigation
                            badgeSx={{
                                border: `1px solid ${theme[themeStyle].tag_white}`,
                                borderRadius: '50%',
                            }}
                        />
                        <Box style={styles.flexColumn} sx={{ gap: '2px', marginLeft: 1.2, minWidth: 0 }}>
                            <Typography
                                style={styles.title}
                                sx={{
                                    display: '-webkit-box',
                                    overflow: 'hidden',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 1,
                                    fontSize: 14,
                                    lineHeight: 1.2,
                                }}>
                                {media.title}
                            </Typography>
                            <Typography style={styles.subtitle}>{createFullName(media.created_by)}</Typography>
                        </Box>
                    </Stack>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.4,
                            color: theme[themeStyle].background_gray,
                            fontSize: 11,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            whiteSpace: 'nowrap',
                        }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography style={styles.date}>
                                <VisibilityOutlinedIcon sx={{ fontSize: 13, mr: 0.4, mb: -0.15 }} />
                                {formatNumberWithK(media.view_count || 0)}
                            </Typography>
                        </Box>
                        <Typography style={styles.date}>
                            {convertMinuteSecondsDays(media.last_seen || media.createdAt)} ago
                        </Typography>
                    </Box>
                </Stack>
            )}
        </Box>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        bgImage: {
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            left: 0,
            width: 'inherit',
            height: 'inherit',
            zIndex: -1,
        },
        flexColumn: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            flex: 1,
        },
        title: {
            fontSize: 14,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
            textAlign: 'left',
            color: theme[colorScheme].filters_white,
        },
        subtitle: {
            fontSize: 12,
            fontWeight: '500',
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
            textAlign: 'left',
            color: theme[colorScheme].background_gray,
            textTransform: 'capitalize',
        },
        date: {
            fontSize: 11,
            fontWeight: '500',
            fontFamily: FONT_ENUM.MAIN_MEDIUM,
            textAlign: 'left',
            color: theme[colorScheme].background_gray,
            display: 'flex',
            alignItems: 'center',
        },
    });

export { MediaCard };
